/**
 * LastPurchasesSettings — Settings › Inventory › Last 5 purchases.
 *
 * Every ingredient's five most recent purchases, exactly as the bills recorded them: date,
 * vendor, quantity in the unit it was received in, the price per unit and the line total.
 * READ ONLY — nothing on this screen changes anything.
 */

import React, { useEffect, useMemo, useState } from 'react';
import http from '../services/httpClient';

const qty = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return Number.isInteger(v) ? String(v) : String(+v.toFixed(3));
};
// Per-gram and per-ml prices live below a rupee; two decimals would print them as ₹0.00.
const rate = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return `₹${Math.abs(v) >= 1 ? v.toFixed(2) : String(+v.toFixed(4))}`;
};
const money = (n) => (n == null || !Number.isFinite(Number(n))
  ? '—'
  : `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const day = (d) => {
  if (!d) return '—';
  const t = new Date(d);
  return Number.isNaN(t.getTime())
    ? String(d).slice(0, 10)
    : t.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const LastPurchasesSettings = ({ onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let alive = true;
    http.get('/purchase-history/last-five')
      .then((res) => {
        if (!res?.success) throw new Error(res?.error || 'Could not load the purchase history');
        if (alive) setData(res);
      })
      .catch((e) => { if (alive) setError(e?.data?.error || e?.message || 'Could not load the purchase history'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const items = useMemo(() => data?.items || [], [data]);
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? items.filter(it => String(it.item_name || '').toLowerCase().includes(q)) : items;
  }, [items, search]);

  return (
    <div style={overlay} role="dialog" aria-label="Last 5 purchases">
      <div style={sheet}>
        <div style={sheetHeader}>
          <div style={{ minWidth: 0, textAlign: 'left' }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#111827' }}>Last 5 purchases</h2>
            <div style={{ ...muted, marginTop: 2 }}>Most recently bought first — each ingredient's last five purchases, newest to oldest</div>
          </div>
          <button type="button" aria-label="Close" style={closeBtn} onClick={onClose}>×</button>
        </div>

        <div style={sheetBody}>
          <input
            type="search"
            placeholder="Search ingredient"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ ...input, width: '100%' }}
          />

          {loading && <div style={{ ...muted, marginTop: 16 }}>Loading purchases…</div>}
          {error && !loading && (
            <div style={{ ...infoBox, background: '#fef2f2', borderColor: '#fecaca', color: '#991b1b', marginTop: 16 }}>
              {error}
            </div>
          )}

          {!loading && !error && (
            <>
              {items.length === 0 && (
                <div style={{ ...muted, marginTop: 16 }}>No purchases recorded yet.</div>
              )}
              {items.length > 0 && visible.length === 0 && (
                <div style={{ ...muted, marginTop: 16 }}>No ingredient matches.</div>
              )}

              <div style={{ marginTop: 12 }}>
                {visible.map((it) => (
                  <div key={it.item_id} style={card} data-testid={`last-purchases-${it.item_id}`}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'baseline' }}>
                      <div style={{ fontWeight: 700, fontSize: 15, color: '#111827', wordBreak: 'break-word', textAlign: 'left' }}>{it.item_name}</div>
                      <div style={{ ...muted, whiteSpace: 'nowrap' }}>
                        {it.purchases.length} purchase{it.purchases.length === 1 ? '' : 's'}
                      </div>
                    </div>
                    {it.purchases.map((p) => (
                      <div key={p.line_item_id} style={row}>
                        <div style={{ minWidth: 0, textAlign: 'left' }}>
                          <div style={{ fontSize: 13.5, fontWeight: 600, color: '#111827' }}>{day(p.received_date)}</div>
                          <div style={{ ...muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {p.vendor_name || 'Vendor not recorded'}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <div style={{ fontSize: 13.5, fontWeight: 700, color: '#111827' }}>{money(p.line_total)}</div>
                          <div style={muted}>
                            {qty(p.quantity)} {p.unit || ''} × {rate(p.unit_cost)}{p.unit ? `/${p.unit}` : ''}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ── styles, inline like the other Settings sheets (PieceGuardSettings) ──
const overlay = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 10001,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12,
};
const sheet = {
  background: '#fff', borderRadius: 14, width: '100%', maxWidth: 640, maxHeight: '94vh',
  display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  fontFamily: 'Roboto, sans-serif',
};
const sheetHeader = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12,
  padding: '16px 18px 12px', borderBottom: '1px solid #e5e7eb',
};
const sheetBody = { padding: '14px 18px 18px', overflowY: 'auto' };
const muted = { fontSize: 12.5, color: '#6b7280', lineHeight: 1.45 };
const input = {
  padding: '9px 11px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box',
};
const infoBox = {
  padding: '10px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e3a8a',
  borderRadius: 10, fontSize: 13, lineHeight: 1.5,
};
const card = {
  border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px', background: '#fff', marginBottom: 10,
};
const row = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12,
  padding: '8px 0', borderTop: '1px solid #f3f4f6', marginTop: 6,
};
const closeBtn = {
  border: 'none', background: 'transparent', fontSize: 26, lineHeight: 1, cursor: 'pointer', color: '#6b7280',
};

export default LastPurchasesSettings;
